import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FORMATIONS, TIER_TABS, TIER_STYLES } from "../../data/formations";
import { scrollToId } from "../../hooks/useLenis";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { PlayCircle, Clock, BookOpen, Award, Check, ArrowRight, Target, Lock } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";

const FormationCard = ({ f, index, onOpen }) => {
  const { t } = useLang();
  return (
  <motion.div
    data-testid={`formation-${f.id}`}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: (index % 3) * 0.06 }}
    className="group flex flex-col overflow-hidden rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] backdrop-blur-xl transition-[border-color] duration-500 hover:border-blue-400/40"
  >
    <div className="flex items-center justify-center border-b border-blue-500/10 bg-blue-500/5 py-8">
      <BookOpen size={38} className="text-blue-400/60 transition-transform duration-500 group-hover:scale-110" />
    </div>
    <div className="flex flex-1 flex-col p-6">
      <div className="mb-3 flex flex-wrap gap-2">
        <span className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${TIER_STYLES[f.tier]}`}>{t(`academy.tabs.${f.tier}`)}</span>
        <span className="rounded-full border border-blue-500/20 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{f.level}</span>
      </div>
      <h3 className="font-display text-xl font-700 tracking-tight text-white">{f.title}</h3>
      <p className="mt-2 flex-1 font-body text-xs leading-relaxed text-[#8AA0C0]">{f.desc}</p>
      <div className="mt-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.12em] text-blue-300/70">
        <span className="flex items-center gap-1.5"><BookOpen size={12} /> {f.lessons} {t("academy.lessons")}</span>
        <span className="flex items-center gap-1.5"><Clock size={12} /> {f.minutes} min</span>
      </div>
      <button
        data-testid={`formation-open-${f.id}`}
        onClick={() => onOpen(f)}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500"
      >
        <PlayCircle size={13} /> {t("academy.start")}
      </button>
    </div>
  </motion.div>
  );
};

const CourseModal = ({ f, open, onClose }) => {
  const { user } = useAuth();
  const { t } = useLang();
  if (!f) return null;
  const chapters = f.course ? f.course.lessons.map((l) => ({ title: l.title, duration: l.duration, points: l.points, task: l.task })) : (f.chapters || []).map((c) => ({ title: c }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="formation-modal" className="max-h-[88vh] max-w-2xl overflow-hidden border-blue-500/25 bg-[#0B1220]/95 p-0 backdrop-blur-2xl">
        <DialogHeader className="border-b border-blue-500/15 px-6 py-5">
          <div className="mb-2 flex flex-wrap gap-2">
            <span className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${TIER_STYLES[f.tier]}`}>{f.tier}</span>
            <span className="rounded-full border border-blue-500/20 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{f.level} · {f.minutes} min</span>
          </div>
          <DialogTitle className="font-display text-2xl font-800 tracking-tighter text-white">{f.title}</DialogTitle>
          <p className="font-body text-sm text-[#8AA0C0]">{f.desc}</p>
        </DialogHeader>
        <div className="max-h-[58vh] space-y-2 overflow-y-auto px-6 py-4">
          {chapters.map((c, i) => (
            <div key={i} className="rounded-xl border border-blue-500/12 bg-blue-400/[0.02] p-4" data-testid={`formation-chapter-${i}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-blue-500/25 bg-blue-500/10 font-mono text-[11px] text-blue-200">{String(i + 1).padStart(2, "0")}</span>
                <div className="font-display text-sm font-700 tracking-tight text-white">{c.title}</div>
                {c.duration && <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.15em] text-blue-300/70">{c.duration}</span>}
              </div>
              {c.points && (
                <ul className="mt-3 space-y-1.5 pl-11">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 font-body text-[13px] text-[#CBD5E1]"><Check size={13} className="mt-0.5 shrink-0 text-blue-400" /> {p}</li>
                  ))}
                </ul>
              )}
              {c.task && <div className="mt-3 ml-11 flex items-start gap-2 rounded-lg border border-blue-500/15 bg-blue-500/5 p-2.5"><Target size={13} className="mt-0.5 shrink-0 text-blue-300" /><span className="font-body text-[12px] text-[#CBD5E1]">{c.task}</span></div>}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-blue-500/15 px-6 py-4">
          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#8AA0C0]">
            <Lock size={12} className="text-blue-300" /> {t("academy.accessIncluded")} {t(`academy.tabs.${f.tier}`)}
          </span>
          <button data-testid="formation-cta" onClick={() => { onClose(); scrollToId("pricing"); }} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500">
            {user?.subscription ? t("academy.continue") : t("academy.unlock")} <ArrowRight size={13} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Academy = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLang();
  const [tab, setTab] = useState("Tous");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const filtered = tab === "Tous" ? FORMATIONS : FORMATIONS.filter((f) => f.tier === tab);
  const openCourse = (f) => { setSelected(f); setOpen(true); };

  return (
    <section id="academy" data-testid="academy-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(37,99,235,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1300px] px-6 lg:px-10">
        <div ref={ref} className="mb-10 max-w-3xl">
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">[ {t("academy.tag")} ]</motion.span>
          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl">
            {t("academy.title")}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.15 }} className="mt-5 font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg">
            {t("academy.subtitle")}
          </motion.p>
        </div>

        {/* Filter tabs */}
        <div className="mb-10 flex flex-wrap gap-2" data-testid="formation-tabs">
          {TIER_TABS.map((tb) => (
            <button
              key={tb}
              data-testid={`formation-tab-${tb.toLowerCase()}`}
              onClick={() => setTab(tb)}
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${tab === tb ? "border-blue-400 bg-blue-600 text-white" : "border-blue-500/20 text-[#CBD5E1] hover:border-blue-400/40 hover:text-white"}`}
            >
              {t(`academy.tabs.${tb}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((f, i) => <FormationCard key={f.id} f={f} index={i} onOpen={openCourse} />)}
          </AnimatePresence>
        </div>

        {/* Certification */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="mt-10 flex flex-col gap-6 rounded-2xl border border-blue-400/40 bg-blue-500/[0.06] p-8 backdrop-blur-xl glow-cyan sm:flex-row sm:items-center sm:justify-between"
          data-testid="course-certification"
        >
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-blue-400/50 bg-blue-500/15"><Award size={22} className="text-blue-300" /></div>
            <div>
              <h4 className="font-display text-xl font-800 tracking-tighter text-white">{t("academy.certTitle")}</h4>
              <p className="mt-2 font-body text-sm text-[#8AA0C0]">{t("academy.certDesc")}</p>
            </div>
          </div>
          <button data-testid="academy-cta" onClick={() => scrollToId("pricing")} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] hover:bg-blue-500">
            {t("academy.certCta")} <ArrowRight size={14} />
          </button>
        </motion.div>
      </div>

      <CourseModal f={selected} open={open} onClose={() => setOpen(false)} />
    </section>
  );
};
